export interface Holiday {
  name: string;
  /** YYYY-MM-DD */
  date: string;
  /** YYYY-MM-DD the day off is actually taken. Fixed-date holidays that fall
   *  on a weekend move to the nearest weekday, so this can land in the
   *  previous year (New Year's Day on a Saturday is observed Dec 31). */
  observed: string;
  /** Whether a typical clinic closes for it (pre-ticked in the editor). */
  closedByDefault: boolean;
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CLOSED_BY_DEFAULT = new Set([
  "New Year's Day",
  "Memorial Day",
  "Independence Day",
  "Labor Day",
  "Thanksgiving",
  "Christmas Day",
]);

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function fmt(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Day-of-month of the nth (1-indexed) given weekday in a month. month: 1-12,
 *  weekday: 0=Sun..6=Sat. */
function nthWeekday(year: number, month: number, weekday: number, n: number): number {
  const firstDow = new Date(year, month - 1, 1).getDay();
  return 1 + ((7 + weekday - firstDow) % 7) + (n - 1) * 7;
}

/** Day-of-month of the last given weekday in a month. */
function lastWeekday(year: number, month: number, weekday: number): number {
  const lastDay = new Date(year, month, 0).getDate();
  const lastDow = new Date(year, month - 1, lastDay).getDay();
  return lastDay - ((7 + lastDow - weekday) % 7);
}

/** Easter Sunday (Gregorian computus). Returns {month, day}. */
function easter(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
}

function toUtc(date: string): Date {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

/** Shift a YYYY-MM-DD date by a number of days (UTC-safe). */
export function shiftDate(date: string, deltaDays: number): string {
  const dt = toUtc(date);
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

/** Day of week (0=Sun..6=Sat) of a YYYY-MM-DD date. */
export function dayOfWeek(date: string): number {
  return toUtc(date).getUTCDay();
}

/** Federal rule for fixed-date holidays: Saturday is observed on Friday,
 *  Sunday on Monday. */
export function observedDate(date: string): string {
  const dow = dayOfWeek(date);
  if (dow === 6) return shiftDate(date, -1);
  if (dow === 0) return shiftDate(date, 1);
  return date;
}

/** The first Mon–Fri date strictly after `date` that isn't in `closedDates`. */
export function nextBusinessDay(date: string, closedDates: Set<string>): string {
  let d = shiftDate(date, 1);
  while (dayOfWeek(d) === 0 || dayOfWeek(d) === 6 || closedDates.has(d)) d = shiftDate(d, 1);
  return d;
}

/** "Thursday, November 26" */
export function formatLongDate(date: string): string {
  const dt = toUtc(date);
  return `${WEEKDAYS[dt.getUTCDay()]}, ${MONTHS[dt.getUTCMonth()]} ${dt.getUTCDate()}`;
}

/** "Thu, Nov 26" */
export function formatShortDate(date: string): string {
  const dt = toUtc(date);
  return `${WEEKDAYS[dt.getUTCDay()].slice(0, 3)}, ${MONTHS[dt.getUTCMonth()].slice(0, 3)} ${dt.getUTCDate()}`;
}

/** US federal + Hawaii state holidays for a year, sorted by date. */
export function usHolidays(year: number): Holiday[] {
  const e = easter(year);
  const easterDate = fmt(year, e.month, e.day);

  const holiday = (name: string, date: string, observed: string): Holiday => ({
    name,
    date,
    observed,
    closedByDefault: CLOSED_BY_DEFAULT.has(name),
  });
  const fixed = (name: string, month: number, day: number) => {
    const date = fmt(year, month, day);
    return holiday(name, date, observedDate(date));
  };
  const floating = (name: string, date: string) => holiday(name, date, date);

  const holidays: Holiday[] = [
    fixed("New Year's Day", 1, 1),
    floating("Martin Luther King Jr. Day", fmt(year, 1, nthWeekday(year, 1, 1, 3))),
    floating("Presidents' Day", fmt(year, 2, nthWeekday(year, 2, 1, 3))),
    // Hawaii state holidays
    fixed("Prince Kūhiō Day", 3, 26),
    floating("Good Friday", shiftDate(easterDate, -2)),
    fixed("King Kamehameha I Day", 6, 11),
    floating("Statehood Day", fmt(year, 8, nthWeekday(year, 8, 5, 3))),
    // Federal
    floating("Memorial Day", fmt(year, 5, lastWeekday(year, 5, 1))),
    fixed("Juneteenth", 6, 19),
    fixed("Independence Day", 7, 4),
    floating("Labor Day", fmt(year, 9, nthWeekday(year, 9, 1, 1))),
    fixed("Veterans Day", 11, 11),
    floating("Thanksgiving", fmt(year, 11, nthWeekday(year, 11, 4, 4))),
    fixed("Christmas Day", 12, 25),
    // Common clinic closures that aren't official holidays (off by default).
    // Listing them lets the "We reopen …" date skip them when they're ticked.
    floating("Day after Thanksgiving", shiftDate(fmt(year, 11, nthWeekday(year, 11, 4, 4)), 1)),
    floating("Christmas Eve", fmt(year, 12, 24)),
  ];

  return holidays.sort((a, b) => a.date.localeCompare(b.date));
}
